/**
 * useFileViewer
 *
 * Module-level singleton backing FileViewer, mounted once in App.vue. Any
 * caller opens it on a list of files (a record's attachments, a library page)
 * and an index; the arrow keys then step through that list.
 */
import { computed, ref, shallowRef } from "vue"
import type { AssetFieldsFragment } from "@auger/core/gql/types"

export type ViewerFile = Pick<AssetFieldsFragment, "id" | "name" | "url" | "mime_type"> &
  Partial<Pick<AssetFieldsFragment, "size" | "metadata" | "created_at">>

const files = shallowRef<ViewerFile[]>([])
const index = ref(0)
/** Names where the files came from, e.g. "Acme · Board materials". */
const context = ref<string | null>(null)

const current = computed(() => files.value[index.value] ?? null)
const isOpen = computed(() => current.value !== null)

function open(list: ViewerFile[], at = 0, from: string | null = null) {
  files.value = list
  index.value = Math.min(Math.max(at, 0), Math.max(list.length - 1, 0))
  context.value = from
}

function close() {
  files.value = []
  index.value = 0
  context.value = null
}

function step(by: number) {
  const n = files.value.length
  if (n > 1) index.value = (index.value + by + n) % n
}

export function useFileViewer() {
  return {
    files,
    index,
    context,
    current,
    isOpen,
    open,
    close,
    next: () => step(1),
    prev: () => step(-1),
  }
}
